<script setup lang="ts">
import { NTreeSelect } from 'naive-ui'
import { useAttrs } from 'vue'

const props = withDefaults(defineProps<{
  value?: any
  defaultValue?: any
  options?: any[]
  placeholder?: string
  clearable?: boolean
  filterable?: boolean
  remote?: boolean
  multiple?: boolean
  cascade?: boolean
  checkable?: boolean
  checkStrategy?: 'all' | 'child' | 'parent'
  showPath?: boolean
  maxTagCount?: number | 'responsive'
  separator?: string
  leafOnly?: boolean
  showIrrelevantNodes?: boolean
  virtualScroll?: boolean
  maxHeight?: number
  consistentMenuWidth?: boolean
  showArrow?: boolean
  show?: boolean
  scrollable?: boolean
  size?: 'small' | 'medium' | 'large'
  disabled?: boolean
  loading?: boolean
  onUpdateValue?: (value: any) => void
  onChange?: (value: any) => void
  onUpdateShow?: (show: boolean) => void
  onSearch?: (pattern: string) => void
  onBlur?: () => void
  onFocus?: () => void
  onClear?: () => void
}>(), {
  value: undefined,
  defaultValue: undefined,
  options: () => [],
  placeholder: 'Please Select',
  clearable: false,
  filterable: false,
  remote: false,
  multiple: false,
  cascade: false,
  checkable: false,
  checkStrategy: 'all',
  showPath: false,
  maxTagCount: undefined,
  separator: ' / ',
  leafOnly: false,
  showIrrelevantNodes: true,
  virtualScroll: false,
  maxHeight: 300,
  consistentMenuWidth: true,
  showArrow: true,
  show: undefined,
  scrollable: true,
  size: 'medium',
  disabled: false,
  loading: false,
})

const emit = defineEmits<{
  'update:value': [value: any]
  'change': [value: any]
  'update:show': [show: boolean]
  'search': [pattern: string]
  'blur': []
  'focus': []
  'clear': []
}>()

const attrs = useAttrs()

const onUpdateValue = (value: any) => {
  emit('update:value', value)
}

const onChange = (value: any) => {
  emit('change', value)
}

const onUpdateShow = (show: boolean) => {
  emit('update:show', show)
}

const onSearch = (pattern: string) => {
  emit('search', pattern)
}

const onBlur = () => {
  emit('blur')
}

const onFocus = () => {
  emit('focus')
}

const onClear = () => {
  emit('clear')
}
</script>

<template>
  <NTreeSelect
    :value="value"
    :default-value="defaultValue"
    :options="options"
    :placeholder="placeholder"
    :clearable="clearable"
    :filterable="filterable"
    :remote="remote"
    :multiple="multiple"
    :cascade="cascade"
    :checkable="checkable"
    :check-strategy="checkStrategy"
    :show-path="showPath"
    :max-tag-count="maxTagCount"
    :separator="separator"
    :leaf-only="leafOnly"
    :show-irrelevant-nodes="showIrrelevantNodes"
    :virtual-scroll="virtualScroll"
    :max-height="maxHeight"
    :consistent-menu-width="consistentMenuWidth"
    :show-arrow="showArrow"
    :show="show"
    :scrollable="scrollable"
    :size="size"
    :disabled="disabled"
    :loading="loading"
    v-bind="attrs"
    @update:value="onUpdateValue"
    @change="onChange"
    @update:show="onUpdateShow"
    @search="onSearch"
    @blur="onBlur"
    @focus="onFocus"
    @clear="onClear"
  >
    <template #header>
      <slot name="header" />
    </template>
    <template #action>
      <slot name="action" />
    </template>
    <template #arrow>
      <slot name="arrow" />
    </template>
    <template #empty>
      <slot name="empty" />
    </template>
  </NTreeSelect>
</template>

<style scoped>
/* Custom styles can be added here if needed */
</style>
